import { useCallback, useEffect, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";

export interface InvestmentBudgetTotals {
  budgetExclVat: number;
  releasedExclVat: number;
  /** Voorzien budget min wat er vrijgegeven is. Dit is het bedrag om mee te vergelijken. */
  netBudgetExclVat: number;
  itemCount: number;
}

const EMPTY: InvestmentBudgetTotals = {
  budgetExclVat: 0,
  releasedExclVat: 0,
  netBudgetExclVat: 0,
  itemCount: 0,
};

/**
 * Totaal voorzien budget van de investeringslijnen (`/renovation/investments`), gescoopt
 * op een gebouw wanneer er een gekozen is.
 *
 * Eén aggregatiequery backend-side (`GET /investment/budget-totals`) — de bestaande
 * `building/:id/with-assignments` laadt alle lijnen mét toewijzingen en werkt niet zonder
 * gebouw.
 *
 * Fouten gaan naar Sentry maar niet naar een toast: het budget is aanvullende informatie
 * op de documentenpagina en mag die niet met een foutmelding overladen.
 */
export function useInvestmentBudgetTotals(buildingId?: string): {
  totals: InvestmentBudgetTotals | null;
  isLoading: boolean;
} {
  const { session } = useSession();
  const [totals, setTotals] = useState<InvestmentBudgetTotals | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!session) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (buildingId) params.set("buildingId", buildingId);
      const qs = params.toString();
      const res = await fetchWithAuth(
        `${API_URL}/investment/budget-totals${qs ? `?${qs}` : ""}`,
        { method: "GET" },
        session,
      );
      if (!res.ok) throw new Error(`Budgettotalen ophalen mislukt (${res.status})`);
      const json = await res.json();
      setTotals({
        budgetExclVat: Number(json?.budgetExclVat) || 0,
        releasedExclVat: Number(json?.releasedExclVat) || 0,
        netBudgetExclVat: Number(json?.netBudgetExclVat) || 0,
        itemCount: Number(json?.itemCount) || 0,
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { module: "renovation-documents", action: "investmentBudgetTotals" },
        extra: { buildingId: buildingId ?? null },
      });
      setTotals(EMPTY);
    } finally {
      setIsLoading(false);
    }
  }, [session, buildingId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { totals, isLoading };
}
